import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Check, X, Loader2, Edit3, FileText, Users, Tag, ListChecks } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Project Creator Modal
 * Generates professional project/casting content from natural language description.
 * 
 * Features:
 * - Single input box for describing the project
 * - Preview cards showing: Title, Description, Roles/Requirements, Tags
 * - Actions: Accept & Apply, Edit Manually, Regenerate, Cancel
 * - Only available for Enterprise users
 */
export default function AIProjectCreator({ 
  isOpen, 
  onClose, 
  onApply 
}) {
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(null);

  const handleGenerate = async () => {
    if (!description.trim() || description.trim().length < 10) {
      toast.error("Please describe your project in at least 10 characters");
      return;
    }

    setLoading(true);
    setGenerated(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/ai/generate-project`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          description: description.trim()
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to generate project content");
      }

      setGenerated(data.generated);
      setEditedContent(data.generated);
      toast.success("Project content generated!");
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error(error.message || "Failed to generate content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = () => {
    setGenerated(null);
    setEditedContent(null);
    setEditMode(false);
    handleGenerate();
  };

  const handleApply = () => {
    const contentToApply = editMode ? editedContent : generated;
    onApply(contentToApply);
    handleClose();
    toast.success("AI suggestions applied to your project!");
  };

  const handleClose = () => {
    setDescription("");
    setGenerated(null);
    setEditedContent(null);
    setEditMode(false);
    onClose();
  };

  const handleEditField = (field, value) => {
    setEditedContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditRole = (index, field, value) => {
    setEditedContent(prev => {
      const newRoles = [...(prev.roles || [])];
      newRoles[index] = { ...newRoles[index], [field]: value };
      return { ...prev, roles: newRoles };
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            Create Project with AI
          </DialogTitle>
          <DialogDescription>
            Describe your project or casting call and let AI create professional content for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Input Section */}
          {!generated && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Describe your project
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: We're casting for a Hindi web series about a small-town detective. Need 3 main actors - a male lead in his 30s, a female lead in her 20s, and a villain. Shooting in Mumbai for 3 months starting April. Looking for expressive actors with good Hindi diction..."
                className="min-h-[150px] resize-none"
                data-testid="ai-project-description-input"
              />
              <p className="text-xs text-gray-500">
                Include details about roles needed, project type, location, timeline, and any specific requirements.
              </p>
              
              <Button 
                onClick={handleGenerate}
                disabled={loading || description.trim().length < 10}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                data-testid="ai-generate-project-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Project Content
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Results Section */}
          {generated && (
            <div className="space-y-4">
              {/* Preview Cards */}
              <div className="grid gap-3">
                {/* Title */}
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      <FileText className="w-4 h-4" />
                      Project Title
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-4">
                    {editMode ? (
                      <input
                        type="text"
                        value={editedContent.title}
                        onChange={(e) => handleEditField("title", e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      />
                    ) : (
                      <p className="font-medium text-gray-800">{generated.title}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Description */}
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      <FileText className="w-4 h-4" />
                      Description
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-4">
                    {editMode ? (
                      <Textarea
                        value={editedContent.description}
                        onChange={(e) => handleEditField("description", e.target.value)}
                        className="min-h-[100px] text-sm"
                      />
                    ) : (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{generated.description}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Roles */}
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      <Users className="w-4 h-4" />
                      Roles / Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-2 px-4 space-y-2">
                    {editMode ? (
                      <div className="space-y-2">
                        {editedContent.roles?.map((role, i) => (
                          <div key={i} className="flex gap-2">
                            <input
                              type="text"
                              value={role.role_name}
                              onChange={(e) => handleEditRole(i, "role_name", e.target.value)}
                              placeholder="Role name"
                              className="flex-1 px-2 py-1 border rounded text-sm"
                            />
                            <input
                              type="text"
                              value={role.description}
                              onChange={(e) => handleEditRole(i, "description", e.target.value)}
                              placeholder="Description"
                              className="flex-2 px-2 py-1 border rounded text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {generated.roles?.map((role, i) => (
                          <div key={i} className="bg-white rounded p-2 border border-blue-100">
                            <p className="font-medium text-sm text-blue-800">{role.role_name}</p>
                            <p className="text-xs text-gray-600">{role.description}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Requirements & Tags */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardHeader className="py-2 px-4">
                      <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                        <ListChecks className="w-4 h-4" />
                        Requirements
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-4">
                      {editMode ? (
                        <Textarea
                          value={editedContent.requirements?.join("\n") || ""}
                          onChange={(e) => handleEditField("requirements", e.target.value.split("\n").filter(Boolean))}
                          placeholder="One requirement per line"
                          className="min-h-[80px] text-sm"
                        />
                      ) : (
                        <ul className="text-xs text-gray-700 space-y-1">
                          {generated.requirements?.map((req, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-blue-500 mt-0.5">•</span>
                              {req}
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardHeader className="py-2 px-4">
                      <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                        <Tag className="w-4 h-4" />
                        Tags
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="py-2 px-4">
                      {editMode ? (
                        <input
                          type="text"
                          value={editedContent.tags?.join(", ") || ""}
                          onChange={(e) => handleEditField("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                          placeholder="Comma-separated tags"
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {generated.tags?.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-blue-300 text-blue-700">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button 
                  onClick={handleApply}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="ai-apply-project-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Accept & Apply
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => setEditMode(!editMode)}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="ai-edit-project-btn"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {editMode ? "Preview" : "Edit"}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={loading}
                  className="border-gray-300"
                  data-testid="ai-regenerate-project-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Regenerate
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={handleClose}
                  className="text-gray-500"
                  data-testid="ai-cancel-project-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
